import { useCommunityDetails } from 'atoms/community-details';
import cc from 'classcat';
import { useCommunitySummaries } from 'atoms/community-summaries';
import ROUTES, { DashboardParams } from 'constants/routes';
import React, { CSSProperties, useState, useMemo } from 'react';
import { Link, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import defaultCoverUrl from '../../assets/images/penguin-group.png';
import CameraAltIcon from '!!react-svg-loader!./Camera.svg';
import loaderImage from '../../assets/images/dual-ring-spinner.svg';
import NounGroupIcon from '!!react-svg-loader!../welcome-page/noun-group.svg';

import C from './CommunityOverviewPanel.css';
import IconButton from '@mui/material/IconButton';
import useFileSelector from 'utils/hooks/useFileSelector';
import Avatar from '@mui/material/Avatar';
import FlexRow from 'components/flex/FlexRow';
import CommunitySettingsTrigger from 'components/community-settings-trigger/CommunitySettingsTrigger';
import Tooltip from '@mui/material/Tooltip';
import { shouldUseNewWindow, visitUrl } from 'utils/url';
import { Community } from '@cocoplatform/coco-rtc-shared';
import { useAuthState } from 'utils/auth-state';
import AddIcon from '@mui/icons-material/Add';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import Button from '@mui/material/Button';
import CollectionsIcon from '!!react-svg-loader!./icon--media-library.svg';
import SettingsIcon from '!!react-svg-loader!./icon-community-settings.svg';
import { Plural, Trans, msg, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { AddMembersButton } from 'components/community-members-explorer/CommunityMembersExplorer';
import { Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { LightTooltip } from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import ShareIcon from '@mui/icons-material/Share';
import DoDisturb from '@mui/icons-material/DoDisturb';
import Swal from 'sweetalert2';
import { useModal } from 'mui-modal-provider';
import SignupCoCoModal from 'components/signup-coco-modal/SignupCoCoModal';
import { LoadingButton } from '@mui/lab';
import { httpClient } from '@cocoplatform/coco-rtc-client';

export function CommunityHandleComponent({
  community,
}: {
  community?: Community;
}) {
  const isCommunityMember = community?.roles?.length > 0;
  return (
    <Stack
      width='100%'
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <div style={{ color: 'var(--color-subtext)' }}>@{community?.handle}</div>
      <Box></Box>
      <Stack direction='row'>
        <Box ml={1} />
        {/* {!isCommunityMember && <FollowCommunityButton community={community} />} */}
        <Box></Box>
        <IconButton>
          <ShareIcon
            style={{ width: 16, height: 16, color: 'var(--color-subtext)' }}
            onClick={() => {
              navigator.clipboard.writeText(
                `${window.location.origin}/@${community?.handle}`,
              );

              Swal.fire({
                icon: 'success',
                title: t`Link copied to clipboard`,
                showConfirmButton: false,
                timer: 1500,
              });
            }}
          />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export async function toggleFollowCommunity(
  communityId: Community,
  isFollowing: boolean,
) {
  if (isFollowing) {
    await httpClient.post(`/community/${communityId}/unfollow`, {});
  } else {
    await httpClient.post(`/community/${communityId}/follow`, {});
  }
}

export function FollowCommunityButton(p: {
  className?: string;
  style?: CSSProperties;
  community?: Community;
}) {
  const { isSignedIn } = useAuthState();

  const { showModal } = useModal();

  const { loadCommunityDetails } = useCommunityDetails({
    communityId: p.community?.id,
    autoLoad: false,
  });

  const [loading, setLoading] = useState(false);
  const isFollowing = isSignedIn && p.community?.following;
  return (
    <LoadingButton
      // variant='text'
      size='small'
      loading={loading}
      disabled={loading}
      className={cc({
        [C.addBtn]: true,
        [p.className]: !!p.className,
      })}
      style={p.style}
      onClick={async (e) => {
        if (!isSignedIn) {
          const modal = showModal(SignupCoCoModal, {
            onClose: () => {
              modal.destroy();
            },
          });

          return;
        }

        e.stopPropagation();

        setLoading(true);

        try {
          await toggleFollowCommunity(p.community?.id, isFollowing);
          await loadCommunityDetails();
        } catch (e) {
          Swal.fire({
            icon: 'error',
            title: t`Error`,
            text: t`There was an error following the community.`,
          });
        } finally {
          setLoading(false);
        }
      }}
    >
      {!loading && (
        <>
          {isFollowing ? (
            <DoDisturb style={{ fontSize: 'medium' }} />
          ) : (
            <AddIcon style={{ fontSize: 'medium' }} />
          )}
          &nbsp;
          <Typography>
            {isFollowing ? <Trans>Unfollow</Trans> : <Trans>Follow</Trans>}
          </Typography>
        </>
      )}
    </LoadingButton>
  );
}

export default function CommunityOverviewPanel(p: {
  className?: string;
  style?: CSSProperties;
  community?: Community;
}) {
  const { _ } = useLingui();
  const dashboardParams = useParams<DashboardParams>();
  const communityId = p.community?.id ?? dashboardParams?.communityId;
  const { isSignedIn } = useAuthState();
  const { communities } = useCommunitySummaries({
    autoLoad: isSignedIn,
  });
  const [isUploadingBanner, setUploadingBanner] = useState(false);
  const communityDetailsHandle = useCommunityDetails({
    communityId,
    autoLoad: true,
  });
  const { updateCommunityBanner } = communityDetailsHandle;
  const communityDetails = communityDetailsHandle.community;
  const community = useMemo(() => {
    return (
      p.community ??
      communityDetails ??
      communities?.find((it) => it.id === communityId)
    );
  }, [p.community, communityDetails, communities]);
  const routeMatch = useRouteMatch();
  const history = useHistory();
  const roles = communityDetails?.roles ?? [];

  const isOwner = useMemo(() => {
    return (roles.indexOf('OWNER') ?? -1) >= 0;
  }, [roles]);

  const isManager = useMemo(() => {
    return (roles.indexOf('MANAGER') ?? -1) >= 0;
  }, [roles]);

  const isCommunitiesPage =
    routeMatch.path === ROUTES.COMMUNITY_DASHBOARD_MEMBERS ||
    routeMatch.path === ROUTES.COMMUNITY_DASHBOARD_NEW_MEMBER;

  const isSettingsPage =
    routeMatch.path === ROUTES.COMMUNITY_DASHBOARD_SETTINGS;

  const isMediaLibraryPage =
    routeMatch.path === ROUTES.MEDIA_LIB ||
    routeMatch.path === ROUTES.MEDIA_LIB_SECTION;

  const ccCount = communityDetails?.coCreatorCount;
  const ccLinked =
    communityDetails &&
    isSignedIn &&
    communityDetails.roles.length > 0 &&
    !communityDetails.deactivatedAt &&
    !communityDetails.pendingApproval;

  const { triggerRef } = useFileSelector({
    onSelect: async (file) => {
      setUploadingBanner(true);
      try {
        await updateCommunityBanner(file);
      } finally {
        setUploadingBanner(false);
      }
    },
  });

  if (!communityId) return null;

  return (
    <div className={p.className} style={p.style}>
      <div
        className={C.cover}
        style={{
          backgroundImage: [
            isUploadingBanner ? loaderImage : undefined,
            community?.bannerUrl ?? defaultCoverUrl,
          ]
            .filter(Boolean)
            .map((url) => `url(${url})`)
            .join(','),
          backgroundSize: isUploadingBanner ? '40px 40px, cover' : 'cover',
        }}
      >
        {isOwner && (
          <Tooltip title={_(msg`Recommended aspect ratio: 2:1`)}>
            <IconButton ref={triggerRef} className={C.coverEditIcon}>
              <CameraAltIcon style={{ height: '1rem', width: '1rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div className={C.detailsContainer}>
        <header className={C.primaryHeader}>
          {communityId && (
            <CommunitySettingsTrigger
              communityId={communityId}
              style={{ float: 'right' }}
            />
          )}
          {community?.name}
        </header>

        {community?.handle && (
          <Box mt={1}>
            <CommunityHandleComponent community={communityDetails} />
          </Box>
        )}
        <div style={{ color: 'var(--color-subtext)' }}>
          {community?.description}
        </div>
        {community?.isActive && (
          <>
            {ccCount != null && (
              <FlexRow
                alignItems='center'
                className={cc({
                  [C.navListItem]: true,
                  [C.inactive]: !ccLinked,
                  [C.active]: isCommunitiesPage,
                })}
              >
                <Avatar className={C.avatar}>
                  <NounGroupIcon style={{ width: 20, height: 20 }} />
                </Avatar>
                <>
                  {communityId && (
                    <a
                      onClick={async (e) => {
                        if (!ccLinked) return;
                        const useNewWindow = shouldUseNewWindow(e);
                        await import(
                          'components/community-members-explorer/CommunityMembersExplorer'
                        );
                        visitUrl(`/app/dashboard/${communityId}/members`, {
                          useNewWindow,
                          history,
                        });
                      }}
                      className={C.sidebarLink}
                      style={{
                        cursor: ccLinked ? 'pointer' : 'default',
                      }}
                    >
                      <Plural
                        value={ccCount}
                        one={'# Member'}
                        other={'# Members'}
                      />
                    </a>
                  )}
                </>
                {(isManager || isOwner) && (
                  <div id='add-members-button'>
                    {/* <Button
                      variant='text'
                      className={C.addBtn}
                      onClick={async (e) => {

                      }}
                    >
                      <AddIcon style={{ fontSize: 'medium' }} />
                      &nbsp;
                      <Trans>Add</Trans>
                    </Button> */}
                    <AddMembersButton
                      variant='text'
                      communityId={communityId}
                      className={C.addBtn}
                      theme='contained'
                      onModalOpen={() => {
                        history.push(`/app/dashboard/${communityId}/members`);
                      }}
                    />
                  </div>
                )}
              </FlexRow>
            )}
            {(isOwner || isManager) && (
              <>
                <FlexRow
                  alignItems='center'
                  className={cc({
                    [C.navListItem]: true,
                    [C.active]: isSettingsPage,
                  })}
                >
                  <Avatar className={C.avatar}>
                    <SettingsIcon style={{ width: 16, height: 16 }} />
                  </Avatar>
                  <Link
                    className={C.sidebarLink}
                    to={`/app/dashboard/${communityId}/settings`}
                  >
                    <Trans>Edit Settings</Trans>
                  </Link>
                </FlexRow>
                <FlexRow
                  alignItems='center'
                  className={cc({
                    [C.navListItem]: true,
                    [C.active]: isMediaLibraryPage,
                  })}
                >
                  <Avatar className={C.avatar}>
                    <CollectionsIcon style={{ width: 16, height: 16 }} />
                  </Avatar>
                  <Link
                    className={C.sidebarLink}
                    to={`/app/dashboard/${communityId}/media-lib`}
                  >
                    <Trans>Media Library</Trans>
                  </Link>
                </FlexRow>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
